import React, { useState, useEffect, useRef } from "react";
import {
  AddressElement,
  useStripe as useStripeElements,
  useElements,
} from "@stripe/react-stripe-js";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { MapPin, PlusCircle, Loader2, ChevronDown, AlertTriangle } from "lucide-react";
import { useAuthContext } from "@/lib/AuthProvider";
import { useUserProfile } from "@/context/UserProfileContext";

interface ShippingDetails {
  complete: boolean;
  value: {
    name: string;
    phone: string;
    address: {
      line1: string;
      line2?: string;
      city: string;
      state: string;
      postal_code: string;
      country: string;
    };
  };
}

interface ShippingAddressModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAddressConfirmed: (shippingDetails: ShippingDetails) => void;
  currentAddress?: ShippingDetails;
}

const ShippingAddressModal: React.FC<ShippingAddressModalProps> = ({
  open,
  onOpenChange,
  onAddressConfirmed,
  currentAddress,
}) => {
  const stripeElements = useStripeElements();
  const elements = useElements();
  const { toast } = useToast();
  const { user } = useAuthContext();
  const { userProfile, loading: loadingProfile, addUserAddress } = useUserProfile();
  
  const [formComplete, setFormComplete] = useState(false);
  const [shippingDetails, setShippingDetails] = useState<ShippingDetails | null>(currentAddress || null);
  const [selectedAddressId, setSelectedAddressId] = useState<string | "new">("new");
  const [defaultAddressKey, setDefaultAddressKey] = useState<string>(Date.now().toString());
  const [hasUserSelected, setHasUserSelected] = useState(false);
  
  // Save address states
  const [saveAddress, setSaveAddress] = useState(false);
  const [addressLabel, setAddressLabel] = useState("");
  const [isSavingAddress, setIsSavingAddress] = useState(false);
  
  // Scroll detection states
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const [isScrolledToTop, setIsScrolledToTop] = useState(true);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Get saved addresses from user profile
  const savedAddresses = userProfile?.addresses || [];

  // Get selected address helper function
  const getSelectedAddress = () => {
    if (selectedAddressId === "new") return null;
    return savedAddresses.find(addr => addr.id === selectedAddressId) || null;
  };

  // Auto-select default address on load (like checkout) - only if user hasn't manually selected
  useEffect(() => {
    if (!hasUserSelected && user && savedAddresses.length > 0 && !loadingProfile) {
      // Find default address or use first address
      const defaultAddress = savedAddresses.find(addr => addr.isDefault) || savedAddresses[0];
      if (defaultAddress && selectedAddressId === "new") {
        setSelectedAddressId(defaultAddress.id);
        setDefaultAddressKey(Date.now().toString()); // Force re-render
      }
    } else if (!user || savedAddresses.length === 0) {
      setSelectedAddressId("new");
    }
  }, [user, savedAddresses, loadingProfile, hasUserSelected, selectedAddressId]);

  // Reset states when modal opens/closes
  useEffect(() => {
    if (open) {
      if (currentAddress) {
        setShippingDetails(currentAddress);
        setFormComplete(true);
      } else {
        setShippingDetails(null);
        setFormComplete(false);
      }
      // Reset user selection tracking when modal opens
      setHasUserSelected(false);
      setSaveAddress(false);
      setAddressLabel("");
      // Reset scroll states
      setShowScrollIndicator(false);
      setIsScrolledToTop(true);
      setIsScrolledToBottom(false);
    }
  }, [open, currentAddress]);

  // Check if content overflows and handle scroll detection
  useEffect(() => {
    const checkScrollability = () => {
      const container = scrollContainerRef.current;
      if (container && open) {
        const hasOverflow = container.scrollHeight > container.clientHeight;
        setShowScrollIndicator(hasOverflow);
        
        // Check if scrolled to top
        const isAtTop = container.scrollTop < 5;
        setIsScrolledToTop(isAtTop);
        
        // Check if scrolled to bottom
        const isAtBottom = Math.abs(container.scrollHeight - container.clientHeight - container.scrollTop) < 5;
        setIsScrolledToBottom(isAtBottom);
      }
    };

    if (open) {
      // Check initially and after a delay to allow content to render
      setTimeout(checkScrollability, 100);
      setTimeout(checkScrollability, 500);
      
      const container = scrollContainerRef.current;
      if (container) {
        container.addEventListener('scroll', checkScrollability);
        return () => container.removeEventListener('scroll', checkScrollability);
      }
    }
  }, [open, selectedAddressId, formComplete]);

  const handleAddressSelect = (addressId: string) => {
    setSelectedAddressId(addressId);
    setHasUserSelected(true); // Mark that user has made a manual selection
    
    if (addressId === "new") {
      // Clear shipping details for new address
      setShippingDetails(null);
      setFormComplete(false);
      // Reset the AddressElement by updating the key
      setDefaultAddressKey(Date.now().toString());
    } else {
      // Find the selected address
      const address = savedAddresses.find(addr => addr.id === addressId);
      if (address) {
        // Convert saved address to shipping details format
        const convertedAddress = {
          complete: true,
          value: {
            name: address.fullName,
            phone: address.phoneNumber || "",
            address: {
              line1: address.line1,
              line2: address.line2 || "",
              city: address.city,
              state: address.state,
              postal_code: address.zipCode,
              country: address.country,
            }
          }
        };
        
        setShippingDetails(convertedAddress);
        setFormComplete(true);
        
        // Force AddressElement re-render with new default values
        setDefaultAddressKey(Date.now().toString());
      }
    }
  };

  const handleAddressChange = (event: any) => {
    if (event.complete && event.value) {
      const shippingValue = {
        name: event.value.name,
        phone: event.value.phone || "",
        address: {
          line1: event.value.address.line1,
          line2: event.value.address.line2 || "",
          city: event.value.address.city,
          state: event.value.address.state,
          postal_code: event.value.address.postal_code,
          country: event.value.address.country,
        }
      };
      setShippingDetails({ complete: true, value: shippingValue });
      setFormComplete(true);
    } else {
      setFormComplete(false);
    }
  };

  const handleConfirmAddress = async () => {
    if (!formComplete || !shippingDetails) {
      // Enhanced error messaging with scroll guidance
      const hasOverflow = scrollContainerRef.current && 
        scrollContainerRef.current.scrollHeight > scrollContainerRef.current.clientHeight;
      
      let description = "Please complete all required fields in the shipping address form.";
      
      if (hasOverflow && !isScrolledToBottom) {
        description += " Make sure to scroll through the entire form to fill in all fields.";
      }
      
      // Add specific field guidance
      if (selectedAddressId === "new") {
        description += " Required fields include: Name, Address Line 1, City, State/Province, Postal Code, Country, and Phone Number.";
      }
      
      toast({
        title: "Address Information Required",
        description,
        variant: "destructive",
        duration: 8000, // Show longer for detailed message
      });
      
      // Auto-scroll to top of form to help user find missing fields
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTo({ 
          top: 0, 
          behavior: 'smooth' 
        });
      }
      
      return;
    }

    // Save address if user requested and is authenticated
    if (user && saveAddress && selectedAddressId === "new" && addressLabel.trim()) {
      try {
        setIsSavingAddress(true);
        
        const addressValue = shippingDetails.value;
        const formattedAddress = {
          label: addressLabel.trim(),
          fullName: addressValue.name,
          line1: addressValue.address.line1,
          line2: addressValue.address.line2 || "",
          city: addressValue.address.city,
          state: addressValue.address.state,
          zipCode: addressValue.address.postal_code,
          country: addressValue.address.country,
          phoneNumber: addressValue.phone || "",
          isDefault: savedAddresses.length === 0, // Set as default if it's the first address
        };

        await addUserAddress(formattedAddress);
        
        toast({
          title: "Address Saved",
          description: "Your address has been saved for future orders.",
        });
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to save address, but you can continue with checkout.",
          variant: "destructive",
        });
      } finally {
        setIsSavingAddress(false);
      }
    }

    // Confirm the address
    onAddressConfirmed(shippingDetails);
    onOpenChange(false);
  };

  // Render the address selector component
  const renderAddressSelector = () => {
    if (!user || loadingProfile) return null;
    
    if (savedAddresses.length === 0) return null;
    
    return (
      <div className="mb-4">
        <div className="flex justify-between items-center mb-2">
          <Label className="text-sm font-medium">Select Shipping Address</Label>
        </div>
        <Select 
          value={selectedAddressId} 
          onValueChange={handleAddressSelect}
        >
          <SelectTrigger className="w-full bg-white border-gray-200 focus:ring-jamaica-green">
            <SelectValue placeholder="Select an address" />
          </SelectTrigger>
          <SelectContent 
            className="z-[10002] max-h-[200px]"
            position="popper"
            sideOffset={4}
            alignOffset={0}
          >
            {savedAddresses.map((address) => (
              <SelectItem 
                key={address.id} 
                value={address.id}
                className="flex items-center py-3"
              >
                <div className="flex items-center">
                  <MapPin className="h-4 w-4 mr-2 text-gray-400" />
                  <span>{address.label}</span>
                  {address.isDefault && (
                    <Badge className="ml-2 bg-jamaica-green">Default</Badge>
                  )}
                </div>
              </SelectItem>
            ))}
            <SelectItem value="new" className="border-t border-gray-100 mt-1 pt-2">
              <div className="flex items-center text-jamaica-green">
                <PlusCircle className="h-4 w-4 mr-2" />
                <span>Use a new address</span>
              </div>
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
    );
  };

  // Render save address option
  const renderSaveAddressOption = () => {
    if (!user || !formComplete) return null;
    
    // Show save option if user has no saved addresses OR if they selected "new" address
    const shouldShowSaveOption = savedAddresses.length === 0 || selectedAddressId === "new";
    
    if (!shouldShowSaveOption) return null;
    
    return (
      <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
        <div className="flex items-center space-x-2 mb-3">
          <Switch
            id="save-address"
            checked={saveAddress}
            onCheckedChange={setSaveAddress}
          />
          <Label htmlFor="save-address" className="text-sm">
            Save this address for future orders
          </Label>
        </div>
        
        {saveAddress && (
          <div className="mt-3">
            <Label htmlFor="address-label" className="text-sm font-medium">
              Address Label (e.g., Home, Work, etc.)
            </Label>
            <Input
              id="address-label"
              type="text"
              placeholder="Enter a label for this address"
              value={addressLabel}
              onChange={(e) => setAddressLabel(e.target.value)}
              className="mt-1"
              maxLength={50}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <style>{`
        .hide-scrollbar {
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-xl font-bold text-gray-900 flex items-center">
              <MapPin className="h-5 w-5 mr-2 text-jamaica-green" />
              Shipping Address
            </DialogTitle>
          </DialogHeader>
          
          <div className="relative">
            {/* Top fade gradient - only show when not at top */}
            {showScrollIndicator && !isScrolledToTop && (
              <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-white to-transparent h-6 pointer-events-none" />
            )}
            
            {/* Bottom scroll indicator and fade */}
            {showScrollIndicator && !isScrolledToBottom && (
              <div className="absolute bottom-0 left-0 right-0 z-10 bg-gradient-to-t from-white to-transparent h-12 flex items-end justify-center pointer-events-none">
                <div className="bg-jamaica-green/90 text-white text-xs px-3 py-2 rounded-full shadow-lg flex items-center gap-1 mb-2 animate-bounce">
                  <ChevronDown className="h-3 w-3" />
                  More fields below
                </div>
              </div>
            )}
            
            <div 
              ref={scrollContainerRef}
              className="flex-1 overflow-y-auto hide-scrollbar p-4 border border-gray-200 rounded-lg bg-gray-50/30 max-h-96"
            >
              <div className="space-y-4">
            {/* Address Selector */}
            {renderAddressSelector()}
            
            {/* Address Form - Show for both new and existing addresses */}
            {!stripeElements ? (
              <div className="flex items-center justify-center py-8 bg-white rounded-lg border">
                <div className="animate-spin h-6 w-6 border-2 border-jamaica-green border-t-transparent rounded-full mr-2"></div>
                <span className="text-gray-600">Loading...</span>
              </div>
            ) : (
              <div className="space-y-4">
                <Label className="text-sm font-medium">
                  {selectedAddressId === "new" ? "Enter Shipping Address" : "Confirm Shipping Address"}
                </Label>
                <AddressElement
                  key={defaultAddressKey}
                  options={{
                    mode: "shipping",
                    fields: { phone: "always" },
                    validation: {
                      phone: { required: "always" }
                    },
                    // Set default values based on selection (like checkout)
                    defaultValues: selectedAddressId === "new" ? {
                      name: '',
                      address: {
                        line1: '',
                        line2: '',
                        city: '',
                        state: '',
                        postal_code: '',
                        country: 'US',
                      },
                      phone: '',
                    } : (() => {
                      const selectedAddress = getSelectedAddress();
                      return selectedAddress ? {
                        name: selectedAddress.fullName,
                        address: {
                          line1: selectedAddress.line1,
                          line2: selectedAddress.line2 || '',
                          city: selectedAddress.city,
                          state: selectedAddress.state,
                          postal_code: selectedAddress.zipCode,
                          country: selectedAddress.country,
                        },
                        phone: selectedAddress.phoneNumber || '',
                      } : undefined;
                    })()
                  }}
                  onChange={handleAddressChange}
                />
              </div>
            )}
            
            {/* Save Address Option */}
            {renderSaveAddressOption()}
              </div>
            </div>
          </div>
        
        {/* Helper text for incomplete forms */}
        {!formComplete && selectedAddressId === "new" && (
          <div className="flex items-center justify-center gap-2 py-2 text-sm text-amber-600 bg-amber-50 rounded-lg border border-amber-200">
            <AlertTriangle className="h-4 w-4" />
            Complete all required fields above, then click "Confirm Address"
          </div>
        )}
        
        {/* Action Buttons - Fixed Footer - Always Visible */}
        <div className="flex gap-3 pt-4 border-t border-gray-200 flex-shrink-0 bg-white">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="flex-1"
            disabled={isSavingAddress}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirmAddress}
            className="flex-1 bg-jamaica-green hover:bg-jamaica-green/90 shadow-lg transition-all duration-200"
            disabled={isSavingAddress}
          >
            {isSavingAddress ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <MapPin className="mr-2 h-4 w-4" />
                Confirm Address
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default ShippingAddressModal;
